import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faYoutube } from "@fortawesome/free-brands-svg-icons";
import Header from "../../components/Header";
import FooterV2 from "../../components/FooterV2";
import { APP_LINKS } from "../../constants/app-links";
import { detectPlatform, getRefFromUrl, getStoreUrl, persistReferral } from "../../utils/referral-landing";
import DownloadHeroSection from "./components/download-hero-section";
import DownloadHowItWorks from "./components/download-how-it-works";
import DownloadPerksSection from "./components/download-perks-section";
import DownloadFaqSection from "./components/download-faq-section";
import DownloadStickyCta from "./components/download-sticky-cta";

// Số giây trước khi tự đẩy sang store (chỉ khi link có ?ref= và đang ở mobile).
// Dài hơn mức 1,4s cũ để người mới kịp đọc thông tin — vẫn tự chuyển nếu họ không bấm gì.
const REDIRECT_SECONDS = 5;

function DownloadApp() {
    const dispatch = useDispatch();
    // Mã giới thiệu trên URL (?ref=) — đọc 1 lần. Trang này chỉ load khi CHƯA cài app
    // (đã cài → Universal/App Link đã mở thẳng app trước khi web load).
    const [refCode] = useState(getRefFromUrl);
    const [platform] = useState(detectPlatform);
    const storeUrl = useMemo(() => getStoreUrl(platform), [platform]);
    // null = không đếm ngược (desktop, không có ref, hoặc user đã bấm "Ở lại xem thông tin")
    const [countdown, setCountdown] = useState(null);

    useEffect(() => {
        if (!refCode) return;
        // Giữ mã ref đa kênh (clipboard + cookie + ghi IP ở backend) để app điền lại sau khi cài.
        persistReferral(refCode, dispatch);
        if (storeUrl) setCountdown(REDIRECT_SECONDS);
    }, [refCode, storeUrl, dispatch]);

    useEffect(() => {
        if (countdown == null) return;
        if (countdown <= 0) {
            window.location.href = storeUrl;
            return;
        }
        const t = setTimeout(() => setCountdown((c) => (c == null ? null : c - 1)), 1000);
        return () => clearTimeout(t);
    }, [countdown, storeUrl]);

    return (
        <div className="min-h-dvh flex flex-col bg-surface-sunken">
            <Header />
            <div className="flex-1 pt-16 pb-28 md:pb-20 w-full md:w-[768px] lg:w-[1024px] mx-auto">
                <DownloadHeroSection
                    refCode={refCode}
                    countdown={countdown}
                    onStay={() => setCountdown(null)}
                />

                <DownloadHowItWorks />
                <DownloadPerksSection />
                <DownloadFaqSection />

                {/* Video hướng dẫn */}
                <div className="px-4 mt-4">
                    <a
                        href={APP_LINKS.guideVideo}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-3 bg-surface rounded-2xl p-4 border border-line shadow-sm hover:border-rose-200 active:scale-[0.99] transition-all"
                    >
                        <div className="w-11 h-11 shrink-0 rounded-2xl bg-rose-50 text-rose-500 flex items-center justify-center text-xl">
                            <FontAwesomeIcon icon={faYoutube} />
                        </div>
                        <div className="flex-1">
                            <p className="font-bold text-sm">Xem video hướng dẫn tải app</p>
                            <p className="text-xs text-content-3 mt-0.5">Thao tác nhanh chỉ trong 1 phút</p>
                        </div>
                        <FontAwesomeIcon icon={faYoutube} className="text-content-3" />
                    </a>
                </div>
            </div>
            <FooterV2 />
            <DownloadStickyCta storeUrl={storeUrl} refCode={refCode} />
        </div>
    );
}

export default DownloadApp;
